import { useEffect } from 'react';
import {
  TweakRadio,
  TweakSection,
  TweakSelect,
  TweakSlider,
  TweakToggle,
  TweaksPanel,
} from './TweaksPanel';
import { DEFAULT_TWEAKS, type TweakSettings } from './types';

const STORAGE_KEY = 'tk-tweaks';

interface Props {
  tweaks: TweakSettings;
  onChange: (next: TweakSettings) => void;
  theme: 'light' | 'dark';
}

const loadInitial = (): TweakSettings => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_TWEAKS;
    const parsed = JSON.parse(raw) as Partial<TweakSettings>;
    return { ...DEFAULT_TWEAKS, ...parsed };
  } catch {
    return DEFAULT_TWEAKS;
  }
};

export const initialTweaks = loadInitial();

export const TKTweaks = ({ tweaks, onChange, theme }: Props) => {
  const setTweak = <K extends keyof TweakSettings>(
    keyOrEdits: K | Partial<TweakSettings>,
    val?: TweakSettings[K],
  ) => {
    const edits =
      typeof keyOrEdits === 'object' && keyOrEdits !== null
        ? keyOrEdits
        : { [keyOrEdits as K]: val };
    const next = { ...tweaks, ...(edits as Partial<TweakSettings>) };
    onChange(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    const root = document.documentElement;
    const isDark = theme === 'dark';

    const paletteVars = [
      '--tk-accent',
      '--tk-accent-soft',
      '--tk-bg',
      '--tk-surface',
      '--tk-surface-2',
      '--tk-line',
      '--tk-line-2',
      '--tk-ink',
      '--tk-ink-2',
      '--tk-ink-3',
    ];
    if (isDark) {
      // The dark theme is a fixed terminal palette (black + yellow/green/red)
      // defined in tokens.css. Clear any inline overrides left from light
      // mode so the stylesheet values apply; only radius/density/type tweaks
      // remain live in dark.
      paletteVars.forEach((v) => root.style.removeProperty(v));
    } else {
      const aL = tweaks.accentLightness;
      const aC = tweaks.accentChroma;
      root.style.setProperty('--tk-accent', `oklch(${aL} ${aC} ${tweaks.accentHue})`);
      root.style.setProperty('--tk-accent-soft', `oklch(0.95 ${Math.min(0.06, aC * 0.5)} ${tweaks.accentHue})`);
      const w = tweaks.warmth;
      const wi = tweaks.warmthIntensity;
      root.style.setProperty('--tk-bg', `oklch(0.985 ${0.006 * wi} ${w})`);
      root.style.setProperty('--tk-surface', `oklch(0.998 ${0.004 * wi} ${w})`);
      root.style.setProperty('--tk-surface-2', `oklch(0.965 ${0.008 * wi} ${w})`);
      root.style.setProperty('--tk-line', `oklch(0.9 ${0.01 * wi} ${w})`);
      root.style.setProperty('--tk-line-2', `oklch(0.94 ${0.008 * wi} ${w})`);
      root.style.setProperty('--tk-ink', `oklch(0.22 ${0.015 * wi} ${w - 18})`);
      root.style.setProperty('--tk-ink-2', `oklch(0.45 ${0.012 * wi} ${w - 18})`);
      root.style.setProperty('--tk-ink-3', `oklch(0.62 ${0.012 * wi} ${w - 18})`);
    }

    const r = tweaks.cornerRadius;
    root.style.setProperty('--tk-radius-lg', `${r}px`);
    root.style.setProperty('--tk-radius-md', `${Math.max(6, r - 6)}px`);
    root.style.setProperty('--tk-radius-sm', `${Math.max(4, r - 10)}px`);

    root.setAttribute('data-density', tweaks.density);
    root.setAttribute('data-card-style', tweaks.cardStyle);
    root.style.setProperty('--tk-font-heading', `"${tweaks.fontHeading}"`);
    root.style.setProperty('--tk-heading-weight', String(tweaks.headingWeight));
    root.style.setProperty('--tk-heading-tracking', `${tweaks.headingTracking}em`);
  }, [tweaks, theme]);

  return (
    <TweaksPanel title="Tweaks">
      <TweakSection label="Mood (full looks)">
        <TweakRadio
          label="Combos"
          value=""
          options={[
            { value: 'sunsetBazaar', label: 'Sunset Bazaar' },
            { value: 'spiceMarket', label: 'Spice Market' },
            { value: 'clayCourtyard', label: 'Clay Courtyard' },
            { value: 'desertDawn', label: 'Desert Dawn' },
            { value: 'mehndi', label: 'Mehndi' },
            { value: 'rosewater', label: 'Rosewater' },
            { value: 'goldenHour', label: 'Golden Hour' },
            { value: 'pakistanGreen', label: 'Pakistan Green' },
          ]}
          onChange={(p) => {
            const combos: Record<string, Partial<TweakSettings>> = {
              sunsetBazaar: {
                accentHue: 28,
                accentChroma: 0.16,
                accentLightness: 0.6,
                warmth: 50,
                warmthIntensity: 1.8,
              },
              spiceMarket: {
                accentHue: 38,
                accentChroma: 0.13,
                accentLightness: 0.52,
                warmth: 55,
                warmthIntensity: 2.0,
              },
              clayCourtyard: {
                accentHue: 50,
                accentChroma: 0.09,
                accentLightness: 0.5,
                warmth: 65,
                warmthIntensity: 1.6,
              },
              desertDawn: {
                accentHue: 70,
                accentChroma: 0.13,
                accentLightness: 0.62,
                warmth: 75,
                warmthIntensity: 1.4,
              },
              mehndi: {
                accentHue: 130,
                accentChroma: 0.09,
                accentLightness: 0.42,
                warmth: 78,
                warmthIntensity: 1.2,
              },
              rosewater: {
                accentHue: 18,
                accentChroma: 0.1,
                accentLightness: 0.55,
                warmth: 25,
                warmthIntensity: 1.5,
              },
              goldenHour: {
                accentHue: 80,
                accentChroma: 0.15,
                accentLightness: 0.65,
                warmth: 70,
                warmthIntensity: 1.8,
              },
              pakistanGreen: {
                accentHue: 155,
                accentChroma: 0.1,
                accentLightness: 0.46,
                warmth: 78,
                warmthIntensity: 1.0,
              },
            };
            setTweak(combos[p]);
          }}
        />
      </TweakSection>

      <TweakSection label="Accent">
        <TweakSlider
          label="Hue"
          value={tweaks.accentHue}
          min={0}
          max={360}
          step={1}
          onChange={(v) => setTweak('accentHue', v)}
          unit="°"
        />
        <TweakSlider
          label="Chroma"
          value={tweaks.accentChroma}
          min={0}
          max={0.18}
          step={0.005}
          onChange={(v) => setTweak('accentChroma', v)}
        />
        <TweakSlider
          label="Lightness"
          value={tweaks.accentLightness}
          min={0.3}
          max={0.7}
          step={0.01}
          onChange={(v) => setTweak('accentLightness', v)}
        />
      </TweakSection>

      <TweakSection label="Paper warmth">
        <TweakSlider
          label="Hue"
          value={tweaks.warmth}
          min={40}
          max={130}
          step={1}
          onChange={(v) => setTweak('warmth', v)}
          unit="°"
        />
        <TweakSlider
          label="Intensity"
          value={tweaks.warmthIntensity}
          min={0}
          max={2.5}
          step={0.05}
          onChange={(v) => setTweak('warmthIntensity', v)}
        />
      </TweakSection>

      <TweakSection label="Shape">
        <TweakSlider
          label="Card radius"
          value={tweaks.cornerRadius}
          min={0}
          max={32}
          step={1}
          onChange={(v) => setTweak('cornerRadius', v)}
          unit="px"
        />
        <TweakRadio
          label="Card style"
          value={tweaks.cardStyle}
          options={[
            { value: 'outline', label: 'Outline' },
            { value: 'flat', label: 'Flat' },
            { value: 'soft', label: 'Soft' },
          ]}
          onChange={(v) => setTweak('cardStyle', v as TweakSettings['cardStyle'])}
        />
      </TweakSection>

      <TweakSection label="Layout & density">
        <TweakRadio
          label="Density"
          value={tweaks.density}
          options={[
            { value: 'sparse', label: 'Sparse' },
            { value: 'balanced', label: 'Balanced' },
            { value: 'dense', label: 'Dense' },
          ]}
          onChange={(v) => setTweak('density', v as TweakSettings['density'])}
        />
        <TweakSlider
          label="Hero chart"
          value={tweaks.heroChartHeight}
          min={140}
          max={320}
          step={10}
          onChange={(v) => setTweak('heroChartHeight', v)}
          unit="px"
        />
        <TweakRadio
          label="Movers layout"
          value={tweaks.moverLayout}
          options={[
            { value: 'split', label: 'Split' },
            { value: 'merged', label: 'Merged' },
          ]}
          onChange={(v) => setTweak('moverLayout', v as TweakSettings['moverLayout'])}
        />
      </TweakSection>

      <TweakSection label="Typography">
        <TweakSelect
          label="Heading font"
          value={tweaks.fontHeading}
          options={[
            { value: 'Plus Jakarta Sans', label: 'Plus Jakarta Sans' },
            { value: 'Instrument Serif', label: 'Instrument Serif' },
            { value: 'Geist', label: 'Geist' },
            { value: 'Space Grotesk', label: 'Space Grotesk' },
          ]}
          onChange={(v) => setTweak('fontHeading', v)}
        />
        <TweakSlider
          label="Weight"
          value={tweaks.headingWeight}
          min={400}
          max={800}
          step={100}
          onChange={(v) => setTweak('headingWeight', v)}
        />
        <TweakSlider
          label="Tracking"
          value={tweaks.headingTracking}
          min={-0.05}
          max={0.02}
          step={0.005}
          onChange={(v) => setTweak('headingTracking', v)}
          unit="em"
        />
      </TweakSection>

      <TweakSection label="Motion & detail">
        <TweakToggle
          label="Live ticks"
          value={tweaks.liveTicks}
          onChange={(v) => setTweak('liveTicks', v)}
        />
        <TweakToggle
          label="Sparklines"
          value={tweaks.showSparklines}
          onChange={(v) => setTweak('showSparklines', v)}
        />
        <TweakToggle
          label="Ticker tape"
          value={tweaks.showTickerTape}
          onChange={(v) => setTweak('showTickerTape', v)}
        />
      </TweakSection>
    </TweaksPanel>
  );
};
